<script>
    import { eventGroupState, isSelectedEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
    import {
    environment
} from '@app/store/envStore.svelte.js';
    import { projectState } from "@app/store/projectStore.svelte.js";
    import { lockState } from "@app/store/lockStore.svelte.js";
    import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import { onDestroy, onMount } from "svelte";
    import supportedLanguages from "@cclib/dict/spellcheckLanguages.js";
    import { Circle } from "svelte-loading-spinners";
    import diff from "generic-diff";
    /* Firebase */
    import { functions } from "@app/configs/firebase.js";
    import { httpsCallable } from "firebase/functions";
    import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";
    import convertToHtml from "@cclib/functions/quill/convertToHtml.js";
    import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
    import * as m from "@app/paraglide/messages.js";
    let lang = $state(supportedLanguages[0].languageCode),
        // Local Hunspell, OpenAI (online), System (Win/macOS desktop), Word (Windows + Office).
        engine = $state(environment.online ? "OpenAi" : "Local"), //OpenAi, Local, System, Word
        correctedEvents = $state([]),
        progress = $state(0),
        cancelled = false,
        loading = $state(false),
        selectedCorrection = $state(),
        defaultLang = localStorage.getItem("cc-spellcheck-language-v4"),
        capabilities = $state({ system: false, word: false, systemLanguages: [] });

    let isLocked = $derived(isSelectedEventGroupLocked());
    let showSystemEngine = $derived(environment.desktop && capabilities.system);
    let showWordEngine = $derived(environment.desktop && environment.windows && capabilities.word);

    if (defaultLang) {
        lang = defaultLang;
    }

    onMount(() => {
        if (!environment.desktop || !window.ipcRenderer) {
            return;
        }

        window.ipcRenderer
            .invoke("spellcheck-capabilities")
            .then((result) => {
                capabilities = {
                    system: !!result?.system,
                    word: !!result?.word,
                    systemLanguages: Array.isArray(result?.systemLanguages)
                        ? result.systemLanguages
                        : [],
                };
            })
            .catch((err) => {
                console.log(err);
            });
    });

    onDestroy(() => {
        cancelled = true;
        if (window.ipcRenderer) {
            window.ipcRenderer.invoke("spellcheck-cancel").catch(() => {});
        }
    });

    function isDesktopEngine(currentEngine) {
        return currentEngine === "System" || currentEngine === "Word";
    }

    function engineChunkSize(currentEngine) {
        if (currentEngine === "Local" || currentEngine === "System") {
            return 50;
        }
        return 10;
    }

    function engineParallel(currentEngine, progressValue) {
        if (currentEngine === "Word") {
            return 1;
        }
        if (currentEngine === "Local" || currentEngine === "System") {
            return progressValue > 1 ? 10 : 1;
        }
        return progressValue > 1 ? 10 : 2;
    }

    function escapeForRegex(value) {
        return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    async function startSpellCheck() {
        try {
            if (
                !eventGroupState[projectState.selected] ||
                eventGroupState[projectState.selected].events.length === 0
            ) {
                return;
            }

            if (isSelectedEventGroupLocked()) {
                toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
                return;
            }

            if (isDesktopEngine(engine) && !window.ipcRenderer) {
                toast.push(m.spellcheck_ipc_missing(), { classes: ["toast-danger"] });
                return;
            }

            loading = true;
            cancelled = false;
            correctedEvents = [];
            progress = 0;
            eventGroupState[projectState.selected].selected = [];
            selectedCorrection = undefined;
            console.log(
                "Starting Spell Check Process using " +
                    engine +
                    " engine and " +
                    lang +
                    " language.",
            );
            toast.push(m.spellcheck_starting(), {
                classes: ["toast-info"],
            });

            //Generate list of events with plaintext text check spelling on.
            let events = eventGroupState[projectState.selected].events.map(
                (event) => {
                    return {
                        id: event.id,
                        text: convertToPlainText(event.text, " "),
                    };
                },
            );

            //split Events into groups of 10 (or 50 for local). This allows us to run spellcheck in batches of 10.
            let processGroups = events.reduce((resultArray, item, index) => {
                const chunkIndex = Math.floor(index / engineChunkSize(engine));

                if (!resultArray[chunkIndex]) {
                    resultArray[chunkIndex] = []; // start a new chunk
                }

                resultArray[chunkIndex].push(item);

                return resultArray;
            }, []);

            let completed = 0;
            let total = processGroups.length;
            let failedGroups = 0;
            let successfulGroups = 0;
            while (processGroups.length > 0 && !cancelled) {
                let groupsToProcess = processGroups.splice(
                    0,
                    engineParallel(engine, progress),
                );

                let spellCheckPromises = [];
                groupsToProcess.forEach((group) => {
                    if (engine === "Local") {
                        console.log("Adding spell check promise for local engine");
                        spellCheckPromises.push(
                            new Promise((resolve, reject) => {
                                let spellCheckWorker = new Worker("./assets/js/spellcheckWorker.js");

                                spellCheckWorker.onmessage = (e) => {
                                    console.log("SpellcheckWorker completed");
                                    console.log(e);
                                    spellCheckWorker.terminate();

                                    if (e.data?.error) {
                                        reject(new Error(e.data.error));
                                        return;
                                    }

                                    resolve({ data: e.data });
                                };

                                spellCheckWorker.onerror = (error) => {
                                    spellCheckWorker.terminate();
                                    reject(new Error(error.message || "Local spellcheck worker failed."));
                                };

                                spellCheckWorker.postMessage({
                                    groupId:
                                        eventGroupState[projectState.selected]
                                            .id,
                                    events: JSON.stringify(group),
                                    language: lang,
                                });
                            })
                        );
                    } else if (isDesktopEngine(engine)) {
                        console.log(
                            "Spell Check Group: " +
                                group.length +
                                " Events using " +
                                engine +
                                " engine and " +
                                lang +
                                " language.",
                        );

                        spellCheckPromises.push(
                            window.ipcRenderer.invoke("spellcheck-check", {
                                engine,
                                language: lang,
                                events: group,
                            }).then((data) => {
                                if (data?.error) {
                                    throw new Error(data.error);
                                }
                                return { data };
                            }),
                        );
                    } else {
                        let displayName = supportedLanguages.find(
                            (language) => language.languageCode === lang,
                        );

                        console.log(
                            "Spell Check Group: " +
                                group.length +
                                " Events using " +
                                engine +
                                " engine and " +
                                displayName.displayName +
                                " language.",
                        );

                        console.log(group);

                        spellCheckPromises.push(
                            httpsCallable(functions, "v8SpellCheckerV3")({
                                groupId:
                                    eventGroupState[projectState.selected].id,
                                events: JSON.stringify(group),
                                language: displayName.displayName,
                            }),
                        );
                    }
                });

                let results = await Promise.allSettled(spellCheckPromises);
                results.forEach((res) => {
                    if (
                        res.status === "fulfilled" &&
                        res.value.data
                    ) {
                        let corrections = {
                            events: [],
                        };

                        if (Array.isArray(res.value.data.events)) {
                            corrections.events = res.value.data.events;
                        } else if (res.value.data.response) {
                            corrections = JSON.parse(res.value.data.response);
                        }

                        successfulGroups++;
                        //Filter wordCorrections array in each event to only include wordCorrections where the original word is different from the corrected word
                        corrections.events.forEach((event, i, events) => {
                            let eventIndex = eventGroupState[
                                projectState.selected
                            ].events.findIndex((evt) => {
                                return evt.id === event.id;
                            });

                            if (eventIndex === -1) {
                                events[i].wordCorrections = [];
                                return;
                            }

                            let changes = diff(
                                convertToPlainText(
                                    eventGroupState[projectState.selected]
                                        .events[eventIndex].text,
                                    " ",
                                ),
                                event.correctedText,
                            );

                            if (
                                changes.length === 1 &&
                                !changes[0].added &&
                                !changes[0].removed
                            ) {
                                event.wordCorrections = [];
                            }

                            events[i].wordCorrections =
                                event.wordCorrections.filter(
                                    (wordCorrection) => {
                                        return (
                                            wordCorrection.original !==
                                            wordCorrection.corrected
                                        );
                                    },
                                );
                        });

                        corrections.events = corrections.events.filter(
                            (event) => {
                                return event.wordCorrections.length > 0;
                            },
                        );

                        correctedEvents = [
                            ...correctedEvents,
                            ...corrections.events,
                        ];
                    } else {
                        failedGroups++;
                    }

                    completed++;
                    progress = parseInt((completed / total) * 100);
                });
            }

            if (!cancelled && total > 0 && successfulGroups === 0) {
                throw new Error("Spell check failed for all batches.");
            }

            if (failedGroups > 0) {
                toast.push(`Spell check completed with ${failedGroups} failed batch${failedGroups > 1 ? "es" : ""}.`, {
                    classes: ["toast-warning"],
                });
            }

            finalizeSpellCheck();
        } catch (err) {
            console.log(err, err.message);
            loading = false;
            correctedEvents = [];
            progress = 0;
            selectedCorrection = undefined;
            toast.push(
                m.spellcheck_start_error({ error: err.message }),
                { classes: ["toast-danger"] },
            );
        }
    }

    async function finalizeSpellCheck() {
        //console.log(correctedEvents);
        console.log("Ending Spell Check");
        loading = false;
        progress = 0;
        toast.push(m.spellcheck_completed(), {
            classes: ["toast-success"],
        });
    }

    function acceptAllChanges() {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        correctedEvents.forEach((eventCorrection) => {
            let eventIndex = eventGroupState[
                projectState.selected
            ].events.findIndex((event) => {
                return event.id === eventCorrection.id;
            });

            if (eventIndex > -1) {
                eventCorrection.wordCorrections.forEach((correction) => {
                    const escapedOriginal = escapeForRegex(correction.original);
                    let searchPattern = new RegExp(
                        `\\b${escapedOriginal}\\b|\\B${escapedOriginal}\\B`,
                    );
                    if (
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].text.match(searchPattern)
                    ) {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].text = eventGroupState[
                            projectState.selected
                        ].events[eventIndex].text.replace(
                            searchPattern,
                            correction.corrected,
                        );
                    } else {
                        eventGroupState[projectState.selected].events[
                            eventIndex
                        ].text = convertToHtml(eventCorrection.correctedText);
                        eventGroupState[projectState.selected] = autoFormat(
                            eventGroupState[projectState.selected],
                            eventGroupState[projectState.selected].maxLines,
                            eventGroupState[projectState.selected].maxChars,
                            0,
                            false,
                            [eventIndex],
                        );
                    }
                });
            }
        });

        selectedCorrection = undefined;
        correctedEvents = [];
        progress = 0;
        eventGroupState[projectState.selected].selected = [];
        insertHistoryAction({
            name: "correct event", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });
    }

    function acceptChange() {
        if (!selectedCorrection) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        let eventIndex = eventGroupState[
            projectState.selected
        ].events.findIndex((event) => {
            return event.id === selectedCorrection.id;
        });

        if (eventIndex > -1) {
            //Go through the wordCorrections array and replace the original word with the corrected word in the event text.
            selectedCorrection.wordCorrections.forEach((correction) => {
                //First check to see if the original word in the text. If it is then replace it with the corrected word. If it is not then we'll need to replace the entire text with the corrected text.
                const escapedOriginal = escapeForRegex(correction.original);
                let searchPattern = new RegExp(
                    `\\b${escapedOriginal}\\b|\\B${escapedOriginal}\\B`,
                );
                if (
                    eventGroupState[projectState.selected].events[
                        eventIndex
                    ].text.match(searchPattern)
                ) {
                    eventGroupState[projectState.selected].events[
                        eventIndex
                    ].text = eventGroupState[projectState.selected].events[
                        eventIndex
                    ].text.replace(searchPattern, correction.corrected);
                } else {
                    eventGroupState[projectState.selected].events[
                        eventIndex
                    ].text = convertToHtml(selectedCorrection.correctedText);
                    eventGroupState[projectState.selected] = autoFormat(
                        eventGroupState[projectState.selected],
                        eventGroupState[projectState.selected].maxLines,
                        eventGroupState[projectState.selected].maxChars,
                        0,
                        false,
                        [eventIndex],
                    );
                }
            });

            insertHistoryAction({
                name: "correct event", //action name
                eventGroup: projectState.selected,
                snapshots: [
                    {
                        store: "eventGroupState",
                        value: JSON.stringify(eventGroupState),
                    },
                ],
            });

            ignoreChange();
        }
    }

    function ignoreChange() {
        let eventIndex = correctedEvents.findIndex((event) => {
            return event.id === selectedCorrection.id;
        });

        correctedEvents = correctedEvents.filter((correction) => {
            return correction.id !== selectedCorrection.id;
        });

        if (correctedEvents.length > 0) {
            selectedCorrection = correctedEvents[eventIndex]
                ? correctedEvents[eventIndex]
                : correctedEvents[0];

            let selectedEventIndex = eventGroupState[
                projectState.selected
            ].events.findIndex((event) => {
                return event.id === selectedCorrection.id;
            });

            if (selectedEventIndex > -1) {                        
                eventGroupState[projectState.selected].selected = [
                    selectedEventIndex
                ];
                
                // Dispatch custom event to scroll to the selected event
                window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                    detail: { index: selectedEventIndex }
                }));

                if (lockState.video && player){
                    player.currentTime = eventGroupState[projectState.selected].events[selectedEventIndex].start;
                }
            } else {
                eventGroupState[projectState.selected].selected = [];
            }
        } else {
            selectedCorrection = undefined;
            eventGroupState[projectState.selected].selected = [];
        }
    }

    function updateDefaultDictionary() {
        localStorage.setItem("cc-spellcheck-language-v4", lang);
    }

    function selectEvent() {
        if (selectedCorrection && selectedCorrection.id) {
            let eventIndex = eventGroupState[
                projectState.selected
            ].events.findIndex((event) => {
                return event.id === selectedCorrection.id;
            });            

            window.dispatchEvent(new CustomEvent('scrollToVirtualListItem', {
                detail: { index: eventIndex }
            }));
            
            eventGroupState[projectState.selected].selected = [eventIndex];
            if (lockState.video && player){
                player.currentTime = eventGroupState[projectState.selected].events[eventIndex].start;
            }
        }
    }

    function diffChanges(selectedCorrection) {
        try {
            if (!selectedCorrection) {
                return [];
            }
            let eventIndex = eventGroupState[
                projectState.selected
            ].events.findIndex((event) => {
                return event.id === selectedCorrection.id;
            });

            if (eventIndex === -1) {
                return [];
            }

            let changes = diff(
                convertToPlainText(
                    eventGroupState[projectState.selected].events[eventIndex]
                        .text,
                    " ",
                ),
                selectedCorrection.correctedText,
            );
            // console.log("------DEBUG------");
            // console.log(convertToPlainText(eventGroupState[projectState.selected].events[eventIndex].text, " "));
            // console.log(selectedCorrection.correctedText);
            // console.log(changes);

            return changes
                .map(function (edit) {
                    if (edit.added) {
                        return {
                            type: "added",
                            text: edit.items.join(""),
                        };
                    } else if (edit.removed) {
                        return {
                            type: "removed",
                            text: edit.items.join(""),
                        };
                    } else {
                        return {
                            type: "unchanged",
                            text: edit.items.join(""),
                        };
                    }
                });
        } catch (err) {
            console.log(err, err.message);
            return [];
        }
    }

    let previewSegments = $derived.by(() => diffChanges(selectedCorrection));
</script>

<div class="row">
    <div class="col-12">
        <form aria-label={m.spellcheck_form_label()}>
            <div class="row g-2">
                <div class="mb-2 col-6">
                    <label for="corrections-list" class="form-label"
                        >{m.spellcheck_corrections_label()} {#if correctedEvents.length > 0}
                            <span class="badge bg-danger align-middle" role="status" aria-live="polite"
                                >{correctedEvents.length}</span
                            >
                        {/if}</label
                    >
                    <select
                        id="corrections-list"
                        class="form-select form-select-sm resize-vertical"
                        size="5"
                        bind:value={selectedCorrection}
                        onchange={selectEvent}
                        aria-label={m.spellcheck_corrections_list_label()}
                    >
                        {#each correctedEvents as correction (correction.id)}
                            <option value={correction}
                                >{convertToPlainText(
                                    correction.correctedText,
                                    " ",
                                ).substring(0, 50)}</option
                            >
                        {/each}
                    </select>
                </div>
                <div class="mb-2 col-6">
                    <label for="OriginalText" class="form-label">{m.spellcheck_preview_label()}</label>
                    <div
                        id="HtmlTextPreview"
                        class="bg-light resize-vertical p-1 px-2 rounded textPreview"
                    >
                        {#each previewSegments as segment, index (`${segment.type}-${index}`)}
                            {#if segment.type === "added"}
                                <span class="text-primary fw-bold">{segment.text}</span>
                            {:else if segment.type === "removed"}
                                <span class="text-decoration-line-through text-danger">{segment.text}</span>
                            {:else}
                                {segment.text}
                            {/if}
                        {/each}
                    </div>
                </div>
            </div>
            <div class="row g-2">
                <div class="col-2">
                    <select
                        id="engine-select"
                        class="form-select form-select-sm"
                        disabled={loading}
                        aria-label={m.spellcheck_engine_label()}
                        bind:value={engine}
                        onchange={updateDefaultDictionary}
                    >   
                        <option value="Local">{m.spellcheck_engine_local()}</option>
                        {#if showSystemEngine}
                            <option value="System">{m.spellcheck_engine_system()}</option>
                        {/if}
                        {#if showWordEngine}
                            <option value="Word">{m.spellcheck_engine_word()}</option>
                        {/if}
                        {#if environment.online}
                            <option value="OpenAi">{m.spellcheck_engine_openai()}</option>
                        {/if}
                    </select>
                </div>
                <div class="col-4">
                    <select
                        id="language-select"
                        class="form-select form-select-sm"
                        disabled={loading}
                        aria-label={m.spellcheck_language_label()}
                        bind:value={lang}
                        onchange={updateDefaultDictionary}
                    >
                        {#each supportedLanguages as languageOption (languageOption.languageCode)}
                            <option value={languageOption.languageCode}>{languageOption.displayName}</option>            
                        {/each}
                    </select>
                </div>

                <div class="col-6">
                    <button
                        type="button"
                        id="start-spellcheck"
                        class="btn btn-info text-white btn-sm"
                        title={m.spellcheck_start_title()}
                        disabled={loading || isLocked}
                        onclick={() => startSpellCheck()}
                        aria-label={loading ? m.spellcheck_in_progress_label({ progress }) : m.spellcheck_start_label()}
                    >
                        {#if loading}
                            <div class="float-start p-1" role="status" aria-label={m.spellcheck_loading_label()}>
                                <Circle
                                    size="12"
                                    color="#ffffff"
                                    unit="px"
                                    duration="1s"
                                ></Circle>
                            </div>
                            <span aria-live="polite">{progress}%</span>
                        {:else}
                            <i class="bi bi-spellcheck" aria-hidden="true"></i> {m.spellcheck_run_button()}
                        {/if}
                    </button>

                    <div class="btn-group btn-group-sm float-end" role="group" aria-label={m.spellcheck_actions_label()}>
                        <button
                            type="button"
                            id="accept-change"
                            class="btn btn-primary"
                            onclick={acceptChange}
                            title={m.spellcheck_accept_title()}
                            disabled={!selectedCorrection || isLocked}
                            aria-label={m.spellcheck_accept_label()}
                        ><i class="bi bi-check-lg" aria-hidden="true"></i> {m.spellcheck_ok_button()}</button
                        >
                        <button
                            type="button"
                            id="ignore-change"
                            class="btn btn-outline-dark"
                            onclick={ignoreChange}
                            title={m.spellcheck_skip_title()}
                            disabled={!selectedCorrection}
                            aria-label={m.spellcheck_skip_label()}
                        ><i class="bi bi-x-lg" aria-hidden="true"></i></button
                        >
                        <button
                            type="button"
                            id="accept-all"
                            class="btn btn-outline-primary ms-2"
                            onclick={acceptAllChanges}
                            title={m.spellcheck_accept_all_title()}
                            disabled={correctedEvents.length === 0 || isLocked}
                            aria-label={m.spellcheck_accept_all_label()}
                            ><i class="bi bi-check2-all" aria-hidden="true"></i> {m.spellcheck_all_button()}</button
                        >
                    </div>
                </div>
            </div>
        </form>
    </div>
</div>

<style lang="css">
    #HtmlTextPreview {
        height: 10vh;
        overflow-y: auto;
    }
</style>
